import { createAuthenticatedClient } from "../authenticatedClient";
import { API_BASE_URL } from "../config";
import { safeValidate } from "../safeValidate";
import {
  AgentPolicy,
  AgentPolicyCondition,
  AgentPolicyResponse,
  agentPolicyResponseSchema,
  agentPolicySchema,
  type PolicyAction,
} from "../schemas/agentPolicy.schema";

const agentClient = createAuthenticatedClient(API_BASE_URL);

interface GetPolicyListParams {
  nextPageToken?: string;
  type?: string;
  active?: boolean;
  domain?: string;
  limit?: number;
  searchValue?: string;
}

export interface PolicyPayload {
  name?: string;
  description?: string;
  triggerType?: string;
  isActive?: boolean;
  conditions?: AgentPolicyCondition;
  actions?: PolicyAction[];
  domain?: string;
  isCustom?: boolean;
}

export const agentPolicyApi = {
  getPolicyList: async (
    params?: GetPolicyListParams,
  ): Promise<AgentPolicyResponse> => {
    // Build query string from params
    const queryParams = new URLSearchParams();

    if (params?.type) {
      queryParams.append("type", params.type);
    }
    if (params?.active !== undefined) {
      queryParams.append("active", String(params.active));
    }
    if (params?.domain) {
      queryParams.append("domain", params.domain);
    }
    if (params?.limit) {
      queryParams.append("limit", String(params.limit));
    }
    if (params?.searchValue) {
      queryParams.append("q", params.searchValue);
    }

    if (params?.nextPageToken) {
      queryParams.append("nextToken", params.nextPageToken);
    }

    const queryString = queryParams.toString();
    const url = queryString
      ? `/agent/v1/policy?${queryString}`
      : "/agent/v1/policy";

    const data = await agentClient.get<unknown>(url);
    const validated: AgentPolicyResponse = safeValidate(
      agentPolicyResponseSchema,
      data,
      "getPolicyList",
    ) as AgentPolicyResponse;
    // Type assertion needed because TypeScript doesn't always infer z.output correctly with nested transformations
    return validated;
  },
  getPolicyById: async (policyId: string): Promise<AgentPolicy> => {
    const data = await agentClient.get<unknown>(`/agent/v1/policy/${policyId}`);
    const validated: AgentPolicy = safeValidate(
      agentPolicySchema,
      data,
      "getPolicyById",
    ) as AgentPolicy;
    // Type assertion needed because TypeScript doesn't always infer z.output correctly with nested transformations
    // The zod transform converts regions from string | string[] to string, but TypeScript can't always infer this
    return validated;
  },
  createPolicy: async ({
    payload,
  }: {
    payload: PolicyPayload;
  }): Promise<{ id: string }> => {
    console.log("=====payload", payload);
    const data = await agentClient.post<{ id: string }>(
      "/agent/v1/policy",
      payload,
    );
    return data;
  },
  updatePolicy: async ({
    policyId,
    payload,
  }: {
    policyId: string;
    payload: PolicyPayload;
  }): Promise<{ id: string }> => {
    const data = await agentClient.put<{ id: string }>(
      `/agent/v1/policy/${policyId}`,
      payload,
    );
    return data;
  },
  deletePolicy: async (policyId: string): Promise<void> => {
    await agentClient.delete(`/agent/v1/policy/${policyId}`);
  },
};

export interface EvaluationsPreviewResponse {
  matchingAgentCount: number;
}

export const agentPolicyPreviewApi = {
  getEvaluationsPreview: async (
    payload: PolicyPayload,
  ): Promise<EvaluationsPreviewResponse> => {
    const data = await agentClient.post<EvaluationsPreviewResponse>(
      "/agent/v2/policy/evaluations/preview",
      payload,
    );
    return data;
  },
};

/** Request payload for filter-options (conditions dropdown) */
export interface FilterOptionsRequest {
  target: string;
  targetKey?: string | null;
  searchQuery?: string;
  filters?: Record<string, unknown>;
  pagination?: { page: number; limit: number };
}

/** Option item from filter-options API (flexible for different API shapes) */
export interface FilterOptionItem {
  label?: string;
  value?: string;
  name?: string;
  id?: string;
}

/** Response from filter-options API */
export interface FilterOptionsResponse {
  data?: FilterOptionItem[];
  options?: FilterOptionItem[];
  content?: FilterOptionItem[];
}

/** Normalized option for UI (label + value) */
export interface FilterOption {
  label: string;
  value: string;
}

function normalizeFilterOptions(
  response: FilterOptionsResponse,
): FilterOption[] {
  const raw = response.data ?? response.options ?? response.content ?? [];
  return raw
    .map((item) => {
      const label =
        item.label ?? item.name ?? item.value ?? String(item.id ?? "");
      const value = item.value ?? item.id ?? item.label ?? item.name ?? label;
      return label ? { label: String(label), value: String(value) } : null;
    })
    .filter((o): o is FilterOption => o !== null);
}

export const agentRegistryApi = {
  getFilterOptions: async (
    payload: FilterOptionsRequest,
  ): Promise<FilterOption[]> => {
    const body = {
      target: payload.target,
      targetKey: payload.targetKey ?? null,
      searchQuery: payload.searchQuery ?? "",
      filters: payload.filters ?? {},
      pagination: payload.pagination ?? { page: 1, limit: 20 },
    };
    const data = await agentClient.post<FilterOptionsResponse>(
      "/agent/v2/registry/agents/filter-options",
      body,
    );
    return normalizeFilterOptions(data);
  },
};
